/**
 * Builds the Excel/Word/PDF export for POD adjudication claims (MOR-878).
 *
 * Mirrors the voidables pattern: one `ExportContent` carries both a
 * narrative `markdown` (Word / PDF — a file-note style adjudication record)
 * and tabular `sheets` (Excel), so a single builder covers all three
 * formats. Pass one claim for a per-claim file note, or several for the
 * rows the practitioner selected / the whole schedule.
 *
 * Money is written as real numbers and dates as real Date cells so the
 * practitioner can sort / sum / filter natively in Excel.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import { parseDateCell } from "@/components/jobs/phases/gather/summarization/bankTransactions/buildBankTransactionsExport";
import {
  POD_CREDITOR_TYPE_LABELS,
  POD_STATUS_LABELS,
  POD_SUPPORTING_DOC_LABELS,
  derivePodOutcome,
  derivePodProofSplit,
  fmtMoney,
  fmtDate,
  fmtTimestamp,
  type PodClaimStatus,
  type PodCreditorType,
  type PodRecommendation,
  type PodSupportingDocType,
} from "./podShared";

/** One claim line as returned by `listPodClaimsForExport`. */
export interface PodClaimExportItem {
  date: string | null;
  reference: string | null;
  description: string;
  amount: number;
  sourceDocument: string | null;
}

/** Row shape returned by publicQueries.listPodClaimsForExport. */
export interface PodClaimExportRow {
  claimId: string;
  creditorName: string;
  creditorReference: string | null;
  creditorType: PodCreditorType;
  status: PodClaimStatus;
  requestedAt: number | null;
  receivedAt: number | null;
  podDate: string | null;
  claimedNet: number | null;
  claimedGst: number | null;
  claimedTotal: number;
  securityHeldValue: number | null;
  securityHeldDescription: string | null;
  substantiatedTotal: number;
  estimatedValue: number | null;
  estimatedValueSource: "books" | "pod" | "mixed" | null;
  supportingDocTypes: PodSupportingDocType[];
  supportingDocOther: string | null;
  podExecuted: boolean | null;
  correctlyWitnessed: boolean | null;
  correctlyCompleted: boolean | null;
  recommendation: PodRecommendation | null;
  admittedAmount: number | null;
  rejectedAmount: number | null;
  groundsForRejection: string | null;
  adjudicatedAt: number | null;
  adjudicatedBy: string | null;
  notes: string;
  sourceDocuments: string[];
  claimedItems: PodClaimExportItem[];
  substantiatedItems: PodClaimExportItem[];
  itemsTruncated: boolean;
}

/** Collapse a value onto one readable line; blank/empty comes back as null. */
function inline(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).replace(/\s+/g, " ").trim();
  return text.length > 0 ? text : null;
}

/** The adjudication outcome as one label, or the lifecycle status until then. */
function outcomeLabel(claim: PodClaimExportRow): string {
  // The shared helpers take the claim document's optional (undefined)
  // fields; the export row carries explicit nulls — bridge the two.
  const outcome = derivePodOutcome({
    status: claim.status,
    recommendation: claim.recommendation ?? undefined,
    admittedAmount: claim.admittedAmount ?? undefined,
    rejectedAmount: claim.rejectedAmount ?? undefined,
  });
  return outcome?.label ?? POD_STATUS_LABELS[claim.status];
}

/** `derivePodProofSplit` over the export row's null-carrying fields. */
function proofSplit(claim: PodClaimExportRow) {
  return derivePodProofSplit({
    creditorType: claim.creditorType,
    claimedTotal: claim.claimedTotal,
    securityHeldValue: claim.securityHeldValue ?? undefined,
  });
}

/** Yes / No / blank — the three form checks are tri-state until answered. */
function checkLabel(value: boolean | null): string {
  if (value === null) return "";
  return value ? "Yes" : "No";
}

/** Epoch-ms timestamp as a real Date cell, or blank. */
function timestampCell(ts: number | null): Date | string {
  return ts ? new Date(ts) : "";
}

function supportingDocsLabel(claim: PodClaimExportRow): string {
  const labels = claim.supportingDocTypes.map((t) =>
    t === "other" && claim.supportingDocOther
      ? `Other (${claim.supportingDocOther})`
      : POD_SUPPORTING_DOC_LABELS[t],
  );
  return labels.join("; ");
}

const CLAIM_HEADERS = [
  "Creditor",
  "Type",
  "Reference",
  "Status",
  "POD Date",
  "Requested",
  "Received",
  "Est. Value (Books)",
  "Security Held",
  "Preferred Proof",
  "Unsecured Proof",
  "Total Proof",
  "Claimed Net",
  "Claimed GST",
  "Substantiated",
  "Unproven",
  "Adjudication",
  "Admitted",
  "Rejected",
  "Grounds for Disallowance",
  "Adjudicated On",
  "Adjudicated By",
  "POD Executed",
  "Correctly Witnessed",
  "Correctly Completed",
  "Supporting Documentation",
  "Notes",
  "Source Documents",
];

function claimRows(claims: PodClaimExportRow[]): ExportSheet["rows"] {
  return claims.map((c) => {
    const split = proofSplit(c);
    const variance = c.claimedTotal - c.substantiatedTotal;
    return [
      c.creditorName,
      POD_CREDITOR_TYPE_LABELS[c.creditorType],
      c.creditorReference ?? "",
      POD_STATUS_LABELS[c.status],
      parseDateCell(c.podDate ?? undefined),
      timestampCell(c.requestedAt),
      timestampCell(c.receivedAt),
      c.estimatedValue ?? "",
      split.securityHeld ?? "",
      split.preferred ?? "",
      split.unsecured ?? "",
      c.claimedTotal,
      c.claimedNet ?? "",
      c.claimedGst ?? "",
      c.substantiatedTotal,
      variance > 0.005 ? variance : "",
      outcomeLabel(c),
      c.admittedAmount ?? "",
      c.rejectedAmount ?? "",
      c.groundsForRejection ?? "",
      timestampCell(c.adjudicatedAt),
      c.adjudicatedBy ?? "",
      checkLabel(c.podExecuted),
      checkLabel(c.correctlyWitnessed),
      checkLabel(c.correctlyCompleted),
      supportingDocsLabel(c),
      c.notes,
      c.sourceDocuments.join("; "),
    ];
  });
}

/** The label/value pairs that head every claim note, in reading order. */
function claimFields(c: PodClaimExportRow): Array<[string, string]> {
  const split = proofSplit(c);
  const fields: Array<[string, string]> = [
    ["Creditor", c.creditorName],
    ["Type", POD_CREDITOR_TYPE_LABELS[c.creditorType]],
  ];
  if (c.creditorReference) fields.push(["Reference", c.creditorReference]);
  fields.push(["Status", POD_STATUS_LABELS[c.status]]);
  if (c.podDate) fields.push(["POD date", fmtDate(c.podDate)]);
  if (c.requestedAt) fields.push(["Requested", fmtTimestamp(c.requestedAt)]);
  if (c.receivedAt) fields.push(["Received", fmtTimestamp(c.receivedAt)]);
  fields.push(["Total proof", fmtMoney(c.claimedTotal)]);
  if (c.claimedNet !== null)
    fields.push(["Claimed net", fmtMoney(c.claimedNet)]);
  if (c.claimedGst !== null)
    fields.push(["Claimed GST", fmtMoney(c.claimedGst)]);
  if (split.securityHeld !== null) {
    fields.push([
      "Security held",
      c.securityHeldDescription
        ? `${fmtMoney(split.securityHeld)} — ${c.securityHeldDescription}`
        : fmtMoney(split.securityHeld),
    ]);
  }
  if (split.preferred !== null) {
    fields.push(["Preferred proof", fmtMoney(split.preferred)]);
  }
  if (split.unsecured !== null) {
    fields.push(["Unsecured proof", fmtMoney(split.unsecured)]);
  }
  if (c.estimatedValue !== null) {
    const caveat =
      c.estimatedValueSource === "pod"
        ? " (recorded from this POD — restates the claim)"
        : c.estimatedValueSource === "mixed"
          ? " (partly recorded from this POD)"
          : "";
    fields.push(["Books estimate", `${fmtMoney(c.estimatedValue)}${caveat}`]);
  }
  fields.push(["Substantiated", fmtMoney(c.substantiatedTotal)]);
  const variance = c.claimedTotal - c.substantiatedTotal;
  if (c.claimedTotal > 0 && variance > 0.005) {
    fields.push(["Unproven", fmtMoney(variance)]);
  }
  fields.push(["Adjudication", outcomeLabel(c)]);
  if (c.admittedAmount !== null) {
    fields.push(["Admitted", fmtMoney(c.admittedAmount)]);
  }
  if (c.rejectedAmount !== null) {
    fields.push(["Rejected", fmtMoney(c.rejectedAmount)]);
  }
  if (c.adjudicatedAt) {
    fields.push(["Adjudicated on", fmtTimestamp(c.adjudicatedAt)]);
  }
  if (c.adjudicatedBy) fields.push(["Adjudicated by", c.adjudicatedBy]);
  if (c.podExecuted !== null) {
    fields.push(["POD executed", checkLabel(c.podExecuted)]);
  }
  if (c.correctlyWitnessed !== null) {
    fields.push(["Correctly witnessed", checkLabel(c.correctlyWitnessed)]);
  }
  if (c.correctlyCompleted !== null) {
    fields.push(["Correctly completed", checkLabel(c.correctlyCompleted)]);
  }
  if (c.supportingDocTypes.length > 0) {
    fields.push(["Supporting documentation", supportingDocsLabel(c)]);
  }
  return fields;
}

/** One "- date — description (#ref): $amount (source)" line per claim item. */
function itemLine(item: PodClaimExportItem): string {
  const parts: string[] = [];
  parts.push(item.date ? fmtDate(item.date) : "Date unknown");
  const desc = inline(item.description) ?? "—";
  parts.push(
    item.reference ? `${desc} (#${item.reference})` : desc,
    fmtMoney(item.amount),
  );
  const line = `- ${parts.join(" — ")}`;
  return item.sourceDocument ? `${line} (${item.sourceDocument})` : line;
}

/** One claim rendered as a file note. `level` is the heading depth to start at. */
function claimMarkdown(c: PodClaimExportRow, level: 1 | 2): string[] {
  const h = (depth: number) => "#".repeat(Math.min(level + depth, 4));
  const parts: string[] = [];

  parts.push(`${h(0)} ${c.creditorName} — ${outcomeLabel(c)}`, "");
  for (const [label, value] of claimFields(c)) {
    parts.push(`**${label}:** ${value}`);
  }

  if (c.groundsForRejection) {
    parts.push(
      "",
      `${h(1)} Grounds for disallowance (Form 537)`,
      "",
      c.groundsForRejection,
    );
  }
  if (inline(c.notes)) {
    parts.push("", `${h(1)} Practitioner notes`, "", c.notes.trim());
  }
  if (c.claimedItems.length > 0) {
    parts.push("", `${h(1)} Claim particulars (Form 535)`, "");
    for (const item of c.claimedItems) parts.push(itemLine(item));
  }
  if (c.substantiatedItems.length > 0) {
    parts.push("", `${h(1)} Supporting documentation lines`, "");
    for (const item of c.substantiatedItems) parts.push(itemLine(item));
  }
  if (c.itemsTruncated) {
    parts.push(
      "",
      "_Line items were truncated for export — the job carries more lines than the export reads._",
    );
  }
  if (c.sourceDocuments.length > 0) {
    parts.push("", `${h(1)} Source documents`, "");
    for (const doc of c.sourceDocuments) parts.push(`- ${doc}`);
  }

  return parts;
}

const ITEM_SHEET_HEADERS = [
  "Creditor",
  "Kind",
  "Date",
  "Reference",
  "Description",
  "Amount",
  "Source Document",
];

function itemSheetRows(claims: PodClaimExportRow[]): ExportSheet["rows"] {
  return claims.flatMap((c) =>
    [
      ...c.claimedItems.map((i) => ({ kind: "Claimed", item: i })),
      ...c.substantiatedItems.map((i) => ({ kind: "Substantiated", item: i })),
    ].map(({ kind, item }) => [
      c.creditorName,
      kind,
      parseDateCell(item.date ?? undefined),
      item.reference ?? "",
      item.description,
      item.amount,
      item.sourceDocument ?? "",
    ]),
  );
}

/**
 * File-note export for one POD claim, or for the rows a practitioner has
 * selected. Pass the claims in the order they should read.
 */
export function buildPodClaimNoteExport(
  claims: PodClaimExportRow[],
): ExportContent {
  const single = claims.length === 1 ? claims[0] : null;
  const title = single
    ? `Proof of Debt — ${single.creditorName}`
    : `Proof of Debt Claims — ${claims.length} selected`;

  const body: string[] = [`# ${title}`, ""];
  if (single) {
    // Drop the redundant per-claim heading — the document title already
    // names the creditor.
    body.push(...claimMarkdown(single, 1).slice(2));
  } else {
    const totalClaimed = claims.reduce((sum, c) => sum + c.claimedTotal, 0);
    const totalAdmitted = claims.reduce(
      (sum, c) => sum + (c.admittedAmount ?? 0),
      0,
    );
    body.push(
      `**Claims:** ${claims.length}`,
      `**Total proof:** ${fmtMoney(totalClaimed)}`,
      `**Total admitted:** ${fmtMoney(totalAdmitted)}`,
      "",
    );
    claims.forEach((c, i) => {
      if (i > 0) body.push("", "---");
      body.push("", ...claimMarkdown(c, 2));
    });
  }

  const detailSheet: ExportSheet = single
    ? {
        name: "Claim",
        headers: ["Field", "Value"],
        rows: [
          ...claimFields(single).map(([label, value]) => [label, value]),
          ["Grounds for Disallowance", single.groundsForRejection ?? ""],
          ["Notes", single.notes],
          ["Source Documents", single.sourceDocuments.join("; ")],
        ],
      }
    : {
        name: "POD Claims",
        headers: CLAIM_HEADERS,
        rows: claimRows(claims),
      };

  const sheets: ExportSheet[] = [detailSheet];

  const itemRows = itemSheetRows(claims);
  if (itemRows.length > 0) {
    sheets.push({
      name: "Line Items",
      headers: ITEM_SHEET_HEADERS,
      rows: itemRows,
    });
  }

  return { title, markdown: body.join("\n"), sheets };
}

/** Filesystem-safe base name for a per-claim / selected export. */
export function podClaimNoteFileName(claims: PodClaimExportRow[]): string {
  if (claims.length === 1) {
    const slug = `POD_${claims[0].creditorName}`
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 80);
    return slug.length > 0 ? slug : "POD_Claim";
  }
  return `POD_Claims_${claims.length}_selected`;
}
